<template>
  <div v-if="error" class="alert alert-danger w-100" style="height: fit-content" role="alert">
    {{ error }}
  </div>
  <div v-else class="w-100">
    <section :class="['card border-0 p-4', {'h-100': loading}]" style="min-height: 100%">
      <div class="d-flex justify-content-end">
        <IconButtonGroup>
          <IconButton
            v-if="editor"
            icon-name="material-symbols:edit-square"
            variant="ghost"
            class="news-icon-button"
            @click="navToEdit"
          />
          <IconButton
            icon-name="material-symbols:print"
            variant="ghost"
            @click="generateNewsReport"
          />
        </IconButtonGroup>
      </div>
      <div v-if="loading" class="d-flex justify-content-center align-items-center h-100">
        <AsciiSpinner />
      </div>
      <div v-else :id="`news-${newsPage?.meta?.id}`" class="item-container">
        <NewsItemTemplate v-if="newsPage" :preview-data="newsPage" />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import {NewsItem} from '@/stores/apolloPlatform/news/news';
import config from '@/config';
import {generatePdf} from '@/sphere/pdf';

interface Props {
  newsPageId: number;
  editor?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  editor: false,
});

const notificationsStore = useNotificationsStore();
const newsStore = useNewsStore();
const {newsPage} = storeToRefs(newsStore);
const loading = ref(true);
const error = ref<string | null>(null);

const markNotificationInStoreAsRead = (id: number) => {
  const newsItem = newsStore.newsItems.find((item: NewsItem) => item.meta.id === id);
  if (newsItem) {
    if (newsItem.unread !== undefined) {
      newsItem.unread = false;
    }
  }
};

const getNewsItem = async () => {
  try {
    if (!props.newsPageId) {
      return;
    }
    const response = await newsStore.getNewsItem(props.newsPageId);
    if (response) {
      newsPage.value = response;
      if (newsPage.value?.notification_id && (newsPage.value.unread || false)) {
        await notificationsStore.markNotificationAsRead(newsPage.value.notification_id);
        const newsItem = newsStore.newsItems.find(
          (item) => item.meta.id === newsPage.value?.meta.id,
        );
        if (newsItem) {
          newsItem.unread = false;
        }
      }
    } else {
      error.value = 'Invalid news item ID.';
    }
  } catch (e: any) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
};

const getEditLink = (item: NewsItem) => {
  if (!item.edit_url) return '#';
  return new URL(item.edit_url, config.CMS_ADMIN_URL);
};

const generateNewsReport = async () => {
  const element = document.getElementById(`news-${newsPage.value?.meta.id}`);
  if (element && newsPage.value) {
    await generatePdf(element, newsPage.value.meta.title, 0.65);
  } else {
    console.error('Element not found for generating PDF');
  }
};

const navToEdit = () => {
  if (props.editor && newsPage.value) {
    window.open(getEditLink(newsPage.value), '_blank');
  }
};

onMounted(async () => {
  loading.value = true;
  await getNewsItem();
  if (newsPage.value) {
    markNotificationInStoreAsRead(newsPage.value.meta.id);
  }
});

watch(
  () => props.newsPageId,
  async (newId, oldId) => {
    loading.value = true;
    error.value = null;
    if (newId !== oldId) {
      await getNewsItem();
    }
    loading.value = false;
  },
);
</script>

<style scoped lang="scss">
.item-container :deep(img),
.item-container :deep(div img) {
  max-width: 100%;
  height: auto;
}
</style>
